// Built-in report templates. These ship as-is and can be cloned (via
// reports-store.duplicateReport) into the user's list for editing — the
// originals stay pristine. IDs are stable strings so /reports/<id> URLs
// remain bookmarkable across releases.
//
// Where possible each template demonstrates the "same data, two views"
// idea — a chart for distribution at-a-glance plus a table for full
// detail (e.g. Memory Inspector renders SHOW MEMORY as bar + pie + full
// table).

const CLUSTER_HEALTH = {
  id: 'cluster-health',
  name: 'Cluster Health',
  description: 'Replication peers, raft groups, and where memory is going.',
  builtin: true,
  panels: [
    {
      id: 'cluster-health-kpi',
      title: 'Cluster KPIs',
      sql: 'SHOW METRICS',
      viz: 'kpi',
      width: 'full',
      autoUpdate: false,
      kpiKeys: [
        'dml.queries_total',
        'calvin_dml.routed_total',
        'server.ws_active_connections',
      ],
    },
    {
      id: 'cluster-health-peers',
      title: 'Replication peers',
      sql: 'SHOW PEERS',
      viz: 'table',
      width: 'full',
      autoUpdate: false,
    },
    {
      id: 'cluster-health-memory-share',
      title: 'Memory share',
      sql: 'SHOW MEMORY',
      viz: 'pie',
      width: 'half',
      autoUpdate: false,
      barLabelCol: 'name',
      barValueCol: 'bytes',
      pieLimit: 6,
    },
    {
      id: 'cluster-health-raftgroups',
      title: 'Raft groups',
      sql: 'SHOW RAFTGROUPS',
      viz: 'table',
      width: 'half',
      autoUpdate: false,
    },
  ],
};

// Calvin pipeline phases live as cumulative nanosecond counters in
// SHOW METRICS. Showing them as a pie reveals where time is going;
// showing them as a bar gives the absolute scale. Same data, two
// readings — the report shows both side-by-side as a demo of the
// pattern.
const CALVIN_PHASE_KEYS = [
  'calvin_dml.window_translate_ns',
  'calvin_dml.window_propose_ns',
  'calvin_dml.window_wait_ns',
  'calvin_dml.window_forward_ns',
];

const THROUGHPUT_SNAPSHOT = {
  id: 'throughput-snapshot',
  name: 'Throughput Snapshot',
  description: 'Query / write counters, where Calvin spends its time, and the slow-query ring buffer.',
  builtin: true,
  panels: [
    {
      id: 'throughput-kpi',
      title: 'Throughput KPIs',
      sql: 'SHOW METRICS',
      viz: 'kpi',
      width: 'full',
      autoUpdate: false,
      kpiKeys: [
        'dml.queries_total',
        'dml.queries_failed_total',
        'calvin_dml.routed_total',
        'calvin_dml.calls_total',
        'server.ws_active_connections',
        'prolly.head_cas_retries',
      ],
    },
    {
      id: 'throughput-calvin-share',
      title: 'Calvin pipeline — time share',
      sql: 'SHOW METRICS',
      viz: 'pie',
      width: 'half',
      autoUpdate: false,
      rowLabelIn: CALVIN_PHASE_KEYS,
      pieLimit: 8,
    },
    {
      id: 'throughput-calvin-bar',
      title: 'Calvin pipeline — by phase',
      sql: 'SHOW METRICS',
      viz: 'bar',
      width: 'half',
      autoUpdate: false,
      rowLabelIn: CALVIN_PHASE_KEYS,
      barLimit: 8,
    },
    {
      id: 'throughput-slow',
      title: 'Slow queries',
      sql: 'SHOW SLOW QUERIES',
      viz: 'table',
      width: 'full',
      autoUpdate: false,
      tableLimit: 20,
    },
  ],
};

const STORAGE_SNAPSHOT = {
  id: 'storage-snapshot',
  name: 'Storage Snapshot',
  description: 'Local disk usage, S3 archival state, and per-table roots.',
  builtin: true,
  panels: [
    {
      id: 'storage-disk-kpi',
      title: 'Disk KPIs',
      sql: 'SHOW DISK STATUS',
      viz: 'kpi',
      width: 'full',
      autoUpdate: false,
      kpiKeys: [
        'head_txn',
        'wal_size_bytes',
        'segment_count',
        'segment_total_bytes',
      ],
    },
    {
      id: 'storage-pie',
      title: 'Disk usage breakdown',
      sql: 'SHOW DISK STATUS',
      viz: 'pie',
      width: 'half',
      autoUpdate: false,
      barLabelCol: 'key',
      barValueCol: 'value',
      rowLabelIn: ['wal_size_bytes', 'segment_total_bytes'],
      pieLimit: 4,
    },
    {
      id: 'storage-s3',
      title: 'S3 archival status',
      sql: 'SHOW S3 STATUS',
      viz: 'table',
      width: 'half',
      autoUpdate: false,
    },
    {
      id: 'storage-roots',
      title: 'Per-table roots',
      sql: 'SHOW ROOTS',
      viz: 'table',
      width: 'full',
      autoUpdate: false,
      tableLimit: 20,
    },
  ],
};

// Two views of the same SHOW MEMORY data + the full breakdown — the
// canonical demo of "chart for distribution, table for detail".
const MEMORY_INSPECTOR = {
  id: 'memory-inspector',
  name: 'Memory Inspector',
  description: 'Reporters as a bar of the heaviest, a pie of share, and a full table.',
  builtin: true,
  panels: [
    {
      id: 'memory-bars',
      title: 'Top reporters by bytes',
      sql: 'SHOW MEMORY',
      viz: 'bar',
      width: 'half',
      autoUpdate: false,
      barLabelCol: 'name',
      barValueCol: 'bytes',
      barLimit: 10,
    },
    {
      id: 'memory-pie',
      title: 'Memory share',
      sql: 'SHOW MEMORY',
      viz: 'pie',
      width: 'half',
      autoUpdate: false,
      barLabelCol: 'name',
      barValueCol: 'bytes',
      pieLimit: 6,
    },
    {
      id: 'memory-table',
      title: 'All reporters',
      sql: 'SHOW MEMORY',
      viz: 'table',
      width: 'full',
      autoUpdate: false,
    },
  ],
};

// Collector Metrics — reads the `metrics` table that the per-node
// metrics-drain ships to the collector (B142): a tall/narrow series of
// (time, scope, name, value) samples. View this report against the
// COLLECTOR instance (the one nodes point at via KINEDB_LOG_SINK), where
// the `metrics` table lives — on a plain node it has no such table.
//
// SQL has no now(), so a static report can't do the relative
// `time > now - 1h` window; `ORDER BY time DESC LIMIT N` is the
// static-SQL equivalent of "most recent", and since each panel is a
// single-table SELECT the report auto-updates (WATCH) as fresh samples
// land. `value` is rendered GB/MB by the bar/pie formatter.
//
// The headline panels are `line` viz — the cluster-wide analog of the
// Monitor page's single-server throughput/memory graph, but for ALL nodes
// over time: each `nodename` is its own line (x=time, y=value). reads/sec
// and writes/sec are stored as ready-to-plot RATES by the metrics-drain
// (scope=`throughput`), so the line is the per-second trend directly.
const COLLECTOR_METRICS = {
  id: 'collector-metrics',
  name: 'Collector Metrics',
  description: 'Cluster reads/sec, writes/sec, and memory per node over time — from the collector (view on the collector instance).',
  builtin: true,
  panels: [
    {
      id: 'collector-metrics-reads-line',
      title: 'Reads / sec by node (over time)',
      sql: 'SELECT nodename, time_bucket(time, {{bucket}}) AS time, avg(value) AS value FROM metrics WHERE name = $$reads_per_sec$$ AND time > {{since:15m}} AND time <= {{until}} GROUP BY nodename, time_bucket(time, {{bucket}}) ORDER BY time',
      viz: 'line',
      width: 'half',
      autoUpdate: true,
      autoPoll: true,
      lineSeriesCol: 'nodename',
      lineXCol: 'time',
      lineYCol: 'value',
      lineAgg: 'sum',
    },
    {
      id: 'collector-metrics-writes-line',
      title: 'Writes / sec by node (over time)',
      sql: 'SELECT nodename, time_bucket(time, {{bucket}}) AS time, avg(value) AS value FROM metrics WHERE name = $$writes_per_sec$$ AND time > {{since:15m}} AND time <= {{until}} GROUP BY nodename, time_bucket(time, {{bucket}}) ORDER BY time',
      viz: 'line',
      width: 'half',
      autoUpdate: true,
      autoPoll: true,
      lineSeriesCol: 'nodename',
      lineXCol: 'time',
      lineYCol: 'value',
      lineAgg: 'sum',
    },
    {
      id: 'collector-metrics-cpu-line',
      title: 'CPU % of machine by node — each node’s share (over time)',
      sql: 'SELECT nodename, time_bucket(time, {{bucket}}) AS time, avg(value) AS value FROM metrics WHERE name = $$cpu_percent_of_machine$$ AND time > {{since:15m}} AND time <= {{until}} GROUP BY nodename, time_bucket(time, {{bucket}}) ORDER BY time',
      viz: 'line',
      width: 'half',
      autoUpdate: true,
      autoPoll: true,
      lineSeriesCol: 'nodename',
      lineXCol: 'time',
      lineYCol: 'value',
    },
    {
      id: 'collector-metrics-mem-line',
      title: 'Memory by node — jemalloc.allocated (over time)',
      sql: 'SELECT nodename, time_bucket(time, {{bucket}}) AS time, avg(value) AS value FROM metrics WHERE name = $$jemalloc.allocated$$ AND time > {{since:15m}} AND time <= {{until}} GROUP BY nodename, time_bucket(time, {{bucket}}) ORDER BY time',
      viz: 'line',
      width: 'half',
      autoUpdate: true,
      autoPoll: true,
      lineSeriesCol: 'nodename',
      lineXCol: 'time',
      lineYCol: 'value',
    },
    {
      id: 'collector-metrics-jemalloc-bar',
      title: 'Memory now by node (jemalloc.allocated)',
      sql: 'SELECT nodename, value FROM metrics WHERE name = $$jemalloc.allocated$$ AND time > {{since:15m}} AND time <= {{until}} ORDER BY time DESC LIMIT 12',
      viz: 'bar',
      width: 'half',
      autoUpdate: false,
      barLabelCol: 'nodename',
      barValueCol: 'value',
      barLimit: 12,
    },
    {
      id: 'collector-metrics-raft-table',
      title: 'Per-shard commit index (recent)',
      sql: 'SELECT nodename, scope, value, time FROM metrics WHERE name = $$commit_index$$ AND time > {{since:15m}} AND time <= {{until}} ORDER BY time DESC LIMIT 30',
      viz: 'table',
      width: 'half',
      autoUpdate: false,
      tableLimit: 30,
    },
    // Counter-type SHOW MEMORY components (B177) — reporters whose `used_bytes`
    // is hard-0 (or a constant) and whose REAL signal is the count in ENTRIES:
    // the byte-aware compaction fire counters (`raft.compaction_fires_entries`,
    // `raft.compaction_fires_bytes`, `raft.compaction_aggressive_ticks`). Before
    // metrics-drain shipped a `<name>.entries` row these graphed as a flat 0.
    // Series = the metric NAME (not nodename) so all three fire counters share
    // one chart; avg(value) collapses per-node samples into a cluster trend.
    // Empty until the first cluster run on the new drain build ships `.entries`
    // rows — the line viz renders "No rows", not an error, until then.
    {
      id: 'collector-metrics-compaction-counters-line',
      title: 'Raft compaction activity — fire counters (over time)',
      sql: 'SELECT name, time_bucket(time, {{bucket}}) AS time, avg(value) AS value FROM metrics WHERE name IN ($$raft.compaction_fires_entries.entries$$, $$raft.compaction_fires_bytes.entries$$, $$raft.compaction_aggressive_ticks.entries$$) AND time > {{since:15m}} AND time <= {{until}} GROUP BY name, time_bucket(time, {{bucket}}) ORDER BY time',
      viz: 'line',
      width: 'full',
      autoUpdate: true,
      autoPoll: true,
      lineSeriesCol: 'name',
      lineXCol: 'time',
      lineYCol: 'value',
    },
  ],
};

// Memory Tracking (B143) — the per-node memory ACCOUNTING over time, from the
// same collector `metrics` table (scope=`mem`). The metrics-drain ships every
// SHOW MEMORY component plus the computed summary (`summary.tracked_total`,
// `summary.untracked_gap`) and `process.rss`. The headline is untracked_gap:
// when it climbs, RAM is going somewhere our reporters don't yet name. View on
// the COLLECTOR instance (where the `metrics` table lives).
const MEMORY_TRACKING = {
  id: 'memory-tracking',
  name: 'Memory Tracking',
  description: 'Per-node memory accounting over time — tracked vs allocated vs RSS, and the untracked gap (B143). View on the collector instance.',
  builtin: true,
  panels: [
    {
      id: 'memory-tracking-gap-line',
      title: 'Untracked gap by node (over time) — allocated − tracked',
      sql: 'SELECT nodename, time_bucket(time, {{bucket}}) AS time, avg(value) AS value FROM metrics WHERE name = $$summary.untracked_gap$$ AND time > {{since:15m}} AND time <= {{until}} GROUP BY nodename, time_bucket(time, {{bucket}}) ORDER BY time',
      viz: 'line',
      width: 'half',
      autoUpdate: true,
      autoPoll: true,
      lineSeriesCol: 'nodename',
      lineXCol: 'time',
      lineYCol: 'value',
      lineAgg: 'sum',
    },
    {
      id: 'memory-tracking-allocated-line',
      title: 'jemalloc.allocated by node (over time) — the real heap',
      sql: 'SELECT nodename, time_bucket(time, {{bucket}}) AS time, avg(value) AS value FROM metrics WHERE name = $$jemalloc.allocated$$ AND time > {{since:15m}} AND time <= {{until}} GROUP BY nodename, time_bucket(time, {{bucket}}) ORDER BY time',
      viz: 'line',
      width: 'half',
      autoUpdate: true,
      autoPoll: true,
      lineSeriesCol: 'nodename',
      lineXCol: 'time',
      lineYCol: 'value',
    },
    {
      id: 'memory-tracking-tracked-line',
      title: 'tracked_total by node (over time) — sum of named components',
      sql: 'SELECT nodename, time_bucket(time, {{bucket}}) AS time, avg(value) AS value FROM metrics WHERE name = $$summary.tracked_total$$ AND time > {{since:15m}} AND time <= {{until}} GROUP BY nodename, time_bucket(time, {{bucket}}) ORDER BY time',
      viz: 'line',
      width: 'half',
      autoUpdate: true,
      autoPoll: true,
      lineSeriesCol: 'nodename',
      lineXCol: 'time',
      lineYCol: 'value',
    },
    {
      id: 'memory-tracking-rss-line',
      title: 'process.rss by node (over time) — OS resident set',
      sql: 'SELECT nodename, time_bucket(time, {{bucket}}) AS time, avg(value) AS value FROM metrics WHERE name = $$process.rss$$ AND time > {{since:15m}} AND time <= {{until}} GROUP BY nodename, time_bucket(time, {{bucket}}) ORDER BY time',
      viz: 'line',
      width: 'half',
      autoUpdate: true,
      autoPoll: true,
      lineSeriesCol: 'nodename',
      lineXCol: 'time',
      lineYCol: 'value',
    },
    {
      id: 'memory-tracking-blocks-index-line',
      title: 'storage.blocks_index by node (over time)',
      sql: 'SELECT nodename, time_bucket(time, {{bucket}}) AS time, avg(value) AS value FROM metrics WHERE name = $$storage.blocks_index$$ AND time > {{since:15m}} AND time <= {{until}} GROUP BY nodename, time_bucket(time, {{bucket}}) ORDER BY time',
      viz: 'line',
      width: 'half',
      autoUpdate: true,
      autoPoll: true,
      lineSeriesCol: 'nodename',
      lineXCol: 'time',
      lineYCol: 'value',
    },
    {
      id: 'memory-tracking-gap-now-bar',
      title: 'Untracked gap now by node',
      sql: 'SELECT nodename, value FROM metrics WHERE name = $$summary.untracked_gap$$ AND time > {{since:15m}} AND time <= {{until}} ORDER BY time DESC LIMIT 12',
      viz: 'bar',
      width: 'half',
      autoUpdate: false,
      barLabelCol: 'nodename',
      barValueCol: 'value',
      barLimit: 12,
    },
  ],
};

export const BUILT_IN_REPORTS = [
  CLUSTER_HEALTH,
  THROUGHPUT_SNAPSHOT,
  STORAGE_SNAPSHOT,
  MEMORY_INSPECTOR,
  COLLECTOR_METRICS,
  MEMORY_TRACKING,
];
